import { useState } from 'react';
import { z } from 'zod';
import { useNavigate, Link } from 'react-router';

// UI
import AuthInput from '@components/auth-input';
import Button from '@components/button';
import BackButton from '@components/back-button';

// hooks
import useQueryParam from '@hooks/use-query-param';
import { useVerifyToken } from './utils';

// images
import LogoImg from '@assets/logo.png';

// styles
import css from './styles.module.css';

interface FormInputs {
  password: string;
  confirmPassword: string;
}

const validationSchema = z
  .object({
    password: z.string().min(5, {
      message: 'Password must be 5 or more characters',
    }),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'], // This specifies where the error will be shown
  });

const URL = '/api/auth/change-password';

function CreateNewPasswordPage() {
  const token = useQueryParam('token');
  const navigate = useNavigate();

  useVerifyToken(token);

  const [formData, setFormData] = useState<FormInputs>({
    password: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState<
    Partial<Record<keyof FormInputs, string>>
  >({});

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear the error for the field being edited
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    try {
      validationSchema.parse(formData);

      const response = await fetch(URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...formData, token: token || '' }),
      });

      const data = await response.json();

      console.log('RESULT: ', data);

      if (data.status === 'error') {
        setErrors((prev) => ({ ...prev, password: data.msg }));
      } else if (data.status === 'success') {
        navigate('/password-updated');
      }
    } catch (error) {
      if (error instanceof z.ZodError) {
        const fieldErrors: Partial<Record<keyof FormInputs, string>> = {};
        error.errors.forEach((err) => {
          const field = err.path[0] as keyof FormInputs;
          fieldErrors[field] = err.message;
        });
        setErrors(fieldErrors);
      }
    }
  };

  return (
    <div className={css.changePasswordRoot}>
      <div className={css.contentWrapper}>
        <div>
          <img src={LogoImg} alt="Logo" className={css.logoImg} />

          <BackButton className={css.backButton} iconClassname={css.backIcon} />

          <h3 className={css.title}>Create new password</h3>

          <p className={css.subtitle}>
            To create loyalty cards and send notifications
          </p>

          <form className={css.formWrapper} onSubmit={handleSubmit}>
            <div className={css.inputFields}>
              <AuthInput
                name="password"
                value={formData.password}
                onChange={handleChange}
                type="password"
                error={errors.password}
                placeholder="Password"
                className={css.spaceBelow}
              />

              <AuthInput
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                type="password"
                error={errors.confirmPassword}
                placeholder="Confirm password"
              />
            </div>

            <Button type="submit" size="large" className={css.submitButton}>
              Continue
            </Button>
          </form>

          <p className={css.signUpText}>
            Don't have an account?{' '}
            <Link to="/sign-up" className={css.signUpLink}>
              Sign Up
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default CreateNewPasswordPage;
